import { FocusTrap, FocusTrapFactory } from '@angular/cdk/a11y';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';

import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EmbeddedViewRef,
  EventEmitter,
  HostListener,
  Inject,
  Input,
  OnDestroy,
  Output,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
  DOCUMENT,
  AfterViewInit,
  ChangeDetectorRef,
  ViewChildren,
  QueryList,
} from '@angular/core';
import { TemplateUuids } from '../../../utils/shared-utils';
import { Subject, asyncScheduler, fromEvent } from 'rxjs';
import { subscribeOn, takeUntil, tap } from 'rxjs/operators';
import { TemplatePortal } from '@angular/cdk/portal';
import { TooltipDirective } from "../tooltip";

const KEYCODES = {
  ESC_KEY: 27,
  TAB_KEY: 9,
};

type FocusableElement = ElementRef<HTMLElement> & {
  controlButton?: {
    nativeElement: HTMLElement;
  };
};

const MODAL_INITIAL_FOCUS_DELAY_MS = 600;
@Component({
    selector: 'app-shared-modal',
    templateUrl: './shared-modal.component.html',
    styleUrls: ['./shared-modal.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})

export class SharedModalComponent implements OnDestroy, AfterViewInit {
  focusHandler: (event: FocusEvent) => void;

   @Input('isOpen')
  set isOpen(value: boolean) {
    if (value) {
      this.showModal();
    } else {
      this.hideModal();
    }
  }
  get isOpen() {
    return this._isOpen;
  }

  @Input()
  focusElementOnClose: ElementRef;
  @Input()
  focusElementOnOverlayClick: ElementRef;
  @Input()
  focusOnConfirm = false;
  @Input()
  showTitlebar = true;
  @Input()
  showFooter = true;
  @Input()
  contentText: string;
  @Input()
  titleText: string;
  @Input()
  cancelButtonText: string | null = 'Cancel';
  @Input()
  confirmButtonText: string | null = 'Confirm';
  @Input()
  disableCancel = false;
  @Input()
  disableConfirm = false;
  @Input()
  disableClose = false;
  @Input()
  disableEscape = false;
  @Input()
  footerMessage: string;
  @Input() panelClass = '';
  // Override for default margin-top of 30px.
  @Input()
  marginTop: string | null;
  @Input() removeAriaDescribedByAfterFocus = false;
  private hasInitialFocusOccurred = false;
  @Input('description')
  set description(value: string | TemplateRef<any>) {
    if (value instanceof TemplateRef) {
      this.descriptionTemplateRef = value;
    }

    if (typeof value === 'string') {
      this.descriptionText = value;
    }

    this._description = value;
  }
  get description() {
    return this._description;
  }

  @Output()
  close = new EventEmitter();

  @Output()
  confirm = new EventEmitter();

  @Output()
  cancel = new EventEmitter();

  @Output()
  open = new EventEmitter();

  @Output()
  overlayClick = new EventEmitter();

  @ViewChild('cancelButton') cancelButton: ElementRef;
  @ViewChild('confirmButton') confirmButton: ElementRef;
  @ViewChild('modalBodyRef') modalBodyRef: ElementRef;
  @ViewChildren(TooltipDirective) tooltips: QueryList<TooltipDirective>;

  private _destroy$ = new Subject<void>();
  destroy$ = this._destroy$.asObservable();
  private _isOpen: boolean;
  private _description: string | TemplateRef<any>;
  private _doc: Document;
  private _previouslyFocusedElement: HTMLElement | null = null;
  private modalElement: HTMLElement | null = null;

  descriptionText: string;
  descriptionTemplateRef: TemplateRef<any>;

  templateUUIDs = new TemplateUuids();
  isScrollable = false;

  // variables for attaching template to portal
  @ViewChild('modalTemplatePortal', { static: true })
  modalTemplatePortal: TemplateRef<any>;
  overlayRef: OverlayRef;
  focusTrap: FocusTrap;

  @HostListener('document:keydown', ['$event'])
  keydownEvent(event: KeyboardEvent) {
    this.keydown(event);
  }

  constructor(
    private overlay: Overlay,
    private focusTrapFactory: FocusTrapFactory,
    private viewContainerRef: ViewContainerRef, // Add this line
    private cdr: ChangeDetectorRef,
    @Inject(DOCUMENT) doc: any
  ) {
    this._doc = doc;
  }

  ngAfterViewInit() {
    this.checkIfScrollable();
  }

  @HostListener('window:resize')
  onWindowResize() {
    this.checkIfScrollable();
  }

  private checkIfScrollable() {
    if (this.modalBodyRef) {
      const element = this.modalBodyRef.nativeElement;
      this.isScrollable = element.scrollHeight > element.clientHeight;
    }
  }

  ngOnDestroy() {
    if (this.overlayRef) {
      this.overlayRef.dispose();
      this.focusTrap.destroy();
    }
    this._destroy$.next();
    this._destroy$.unsubscribe();

    if (this.focusHandler && this.modalElement) {
      this.modalElement.removeEventListener('focusin', this.focusHandler);
      this.focusHandler = null;
      this.modalElement = null;
    }
  }

  createModal() {
    let positionStrategy = this.overlay.position().global().centerHorizontally();

    if (this.marginTop) {
      positionStrategy = positionStrategy.top(this.marginTop);
    }

    this.overlayRef = this.overlay.create({
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.block(),
      hasBackdrop: true,
      backdropClass: 'mhe-overlay-dark-backdrop',
      panelClass: this.panelClass,
    });
  }

  showModal() {
    
    // Store the currently focused element before opening the modal
    this._previouslyFocusedElement = this._doc.activeElement as HTMLElement;

    if (!this.overlayRef) {
      this.createModal();
    }

    if (this.modalTemplatePortal) {
        // Create a TemplatePortal from your template reference
    const templatePortal = new TemplatePortal(this.modalTemplatePortal, this.viewContainerRef);
    
    // Attach the TemplatePortal instead of the raw template
    this.overlayRef.attach(templatePortal);
    // Check scrollability after template is attached and rendered
    this.cdr.detectChanges();
    this.checkIfScrollable();

      // create a focus trap
      const modalEl = this.overlayRef.hostElement.querySelector('.modal-dialog') as HTMLElement;
      this.focusTrap = this.focusTrapFactory.create(modalEl);
      const isImportTestModal = this.titleText === 'Import Test';
      if (isImportTestModal) {
        setTimeout(() => {
          this.focusTrap.focusInitialElement();
          //remove aria-describedby to prevent reannouncing after file upload
          if (this.removeAriaDescribedByAfterFocus) {
            this.hasInitialFocusOccurred = true;
            // Better: Listen for the next focus event instead of timeout
            this.setupAriaDescribedByRemoval(modalEl);
          }
        }, MODAL_INITIAL_FOCUS_DELAY_MS);
      } else {
        this.focusTrap.focusInitialElement();
      }
    }

    fromEvent(this._doc, 'click')
      .pipe(
        tap((event) => {
          const modalContent = this.overlayRef && this.overlayRef.hostElement.querySelector('.modal-content');
          const internalClick = modalContent?.contains(event.target as HTMLElement);
          if (!modalContent || internalClick === true || internalClick === undefined) {
            return;
          }
          this.onOverlayClick(event);
        }),
        subscribeOn(asyncScheduler),
        takeUntil(this.overlayRef.detachments())
      )
      .subscribe();
  }

  private setupAriaDescribedByRemoval(modalEl: HTMLElement): void {
    if (!this.removeAriaDescribedByAfterFocus) return;

    const modalContent = modalEl.querySelector('.modal-content');
    if (!modalContent) return;
    this.modalElement = modalEl;
    this.focusHandler = (event: FocusEvent) => {
      if (this.hasInitialFocusOccurred && event.target !== modalContent) {
        modalContent.removeAttribute('aria-describedby');
        modalEl.removeEventListener('focusin', this.focusHandler!);
        this.focusHandler = null;
      }
    };
    modalEl.addEventListener('focusin', this.focusHandler);
  }

  hideModal() {
    this.hasInitialFocusOccurred = false;

    if (this.overlayRef && this.overlayRef.hasAttached()) {
      this.overlayRef.detach();
    }

    if (this.focusTrap) {
      this.focusTrap.destroy();
    }

     // Restore focus to the element that was focused before the modal opened
    this.restoreFocus();
  }

  private restoreFocus(): void {
    const focusableElement = this.focusElementOnClose as FocusableElement;
    if (this.focusElementOnClose && this.focusElementOnClose.nativeElement) {
      // setTimeout to ensure the focus happens after the modal is fully closed
      setTimeout(() => {
        this.focusElementOnClose.nativeElement.focus();
      }, 0);
    } else if (focusableElement?.controlButton && focusableElement?.controlButton?.nativeElement) {
      focusableElement.controlButton.nativeElement.focus();
    } else if (this._previouslyFocusedElement && 
              this._previouslyFocusedElement !== this._doc.body &&
              this._doc.contains(this._previouslyFocusedElement)) {
      // Check if the previously focused element still exists in the DOM
      const elementToFocus = this._previouslyFocusedElement; // Store reference before clearing
      setTimeout(() => {
        elementToFocus.focus();
      }, 0);
    }
    
    // Clear the reference
    this._previouslyFocusedElement = null;
  }

  onCancel() {
    this.cancel.emit(this);
  }

  onConfirm() {
    this.confirm.emit(this);
  }

  onOverlayClick(event: any) {
    if (this.focusElementOnOverlayClick) {
      this.focusElementOnOverlayClick.nativeElement.focus();
    }

    this.overlayClick.emit(this);
  }

  onClose() {
    this.close.emit(this);
  }

  private keydown(event: KeyboardEvent): void {
    if (this.overlayRef?.hasAttached() && event.keyCode === KEYCODES.ESC_KEY && !this.disableEscape) {
      if (!this.checkTooltipPresent()) {
        this.onCancel();
      }
    }
  }

  isValidFooterMessage(footerMessage: string) {
    return typeof footerMessage === 'string' && footerMessage.length > 0;
  }

  getModalDescriptionUUID() {
    return this.templateUUIDs.get(this._description ? 'dialog-description' : 'dialog-content');
  }

  checkTooltipPresent(): boolean {
    return this.tooltips && this.tooltips.some(t => t.isOpen());
  }
}
